/**
 * SET BRAND CURATION STATUS (Admin Tool)
 *
 * Batch tool for marking brands as verified / community / needs-review.
 * Admin pastes a list of brand names (one per line), the tool resolves
 * each name to its brand doc and sets `curationStatus`. Reports per-line
 * results so the admin can see which names didn't match.
 *
 * Companion to the per-brand dropdown on BrandPage — that's good for
 * one-off curation; this is for batch seeding (e.g., "mark my top 50
 * brands as verified" after launch).
 *
 * REQUIRES: isAdmin: true on the user document.
 * Route: /admin/set-brand-curation
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft } from 'lucide-react';
import type { CurationStatus } from '../types/brand';

type StatusChoice = CurationStatus | 'clear';

interface BrandResult {
  inputName: string;
  status: 'ok' | 'not-found' | 'ambiguous' | 'already' | 'error';
  brandId?: string;
  brandName?: string;
  oldStatus?: CurationStatus | null;
  newStatus?: CurationStatus | null;
  detail?: string;
}

interface BrowseBrand {
  id: string;
  name: string;
  curationStatus: CurationStatus | null;
}

export default function SetBrandCurationStatus() {
  const [targetStatus, setTargetStatus] = useState<StatusChoice>('verified');
  const [brandsInput, setBrandsInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<BrandResult[]>([]);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  // Browse-and-pick state. Load brands once when admin lands on the page so
  // we don't re-fetch on every keystroke. Filter is client-side.
  const [allBrands, setAllBrands] = useState<BrowseBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [browseFilter, setBrowseFilter] = useState('');
  const [browseStatusFilter, setBrowseStatusFilter] = useState<
    'all' | 'verified' | 'community' | 'needs-review' | 'default'
  >('all');

  // Use the auth state listener so we don't miss the admin check when
  // Firebase Auth hasn't resolved by first render.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(userDoc.exists() && userDoc.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return unsubscribe;
  }, []);

  // Load the full brand list once admin is confirmed. Reused for filtering
  // and click-to-add below. Skip if not admin (rules likely deny anyway).
  useEffect(() => {
    if (!isAdmin) return;
    setLoadingBrands(true);
    getDocs(collection(db, 'brands'))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({
            id: d.id,
            name: (d.data().name as string) || '',
            curationStatus:
              (d.data().curationStatus as CurationStatus | undefined) ?? null,
          }))
          .filter((b) => b.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setAllBrands(list);
      })
      .catch((err) => console.warn('Failed to load brands:', err))
      .finally(() => setLoadingBrands(false));
  }, [isAdmin]);

  // Apply the search + status filter to the full brand list. No slice cap —
  // the admin needs to scroll through all matches. Rendering ~1.6k buttons
  // is fine for a desktop browser; the status filter or text search will
  // narrow it fast for any real triage workflow.
  const filteredBrowse = (): BrowseBrand[] => {
    const f = browseFilter.trim().toLowerCase();
    return allBrands.filter((b) => {
      if (browseStatusFilter !== 'all') {
        const status = b.curationStatus ?? 'default';
        if (browseStatusFilter !== status) return false;
      }
      if (!f) return true;
      return b.name.toLowerCase().includes(f) || b.id.toLowerCase().includes(f);
    });
  };

  const addBrandToInput = (name: string) => {
    setBrandsInput((prev) => {
      // Avoid duplicate lines.
      const existing = new Set(
        prev.split('\n').map((s) => s.trim().toLowerCase()).filter(Boolean)
      );
      if (existing.has(name.toLowerCase())) return prev;
      return (prev.endsWith('\n') || prev === '' ? prev : prev + '\n') + name;
    });
  };

  // Split textarea into trimmed, deduped, non-empty lines.
  const parsedNames = (): string[] => {
    const seen = new Set<string>();
    return brandsInput
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => {
        if (!s) return false;
        const k = s.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  };

  /**
   * For each input name, find the matching brand doc(s) and either set or
   * clear `curationStatus`. We match by exact `name` first (most specific);
   * if zero matches, we widen to a `nameLower`-equivalent check via the
   * already-loaded set (no second query, since brands are ~1.6k docs).
   */
  const run = async (dryRun: boolean) => {
    if (!currentUser || !isAdmin) return;
    const names = parsedNames();
    if (names.length === 0) return;

    setIsRunning(true);
    setResults([]);
    const out: BrandResult[] = [];

    // Load all brands once. Cheap at our scale (~1.6k docs) and lets us do
    // case-insensitive fallback matching without N queries.
    const allBrandsSnap = await getDocs(collection(db, 'brands'));
    const allBrands = allBrandsSnap.docs.map((d) => ({
      id: d.id,
      name: (d.data().name as string) || '',
      curationStatus: (d.data().curationStatus as CurationStatus | undefined) ?? null,
    }));

    const newStatus: CurationStatus | null =
      targetStatus === 'clear' ? null : targetStatus;

    for (const inputName of names) {
      // 1) Exact match
      let matches = allBrands.filter((b) => b.name === inputName);
      // 2) Case-insensitive fallback if no exact match
      if (matches.length === 0) {
        const lower = inputName.toLowerCase();
        matches = allBrands.filter((b) => b.name.toLowerCase() === lower);
      }

      if (matches.length === 0) {
        out.push({ inputName, status: 'not-found' });
        continue;
      }
      if (matches.length > 1) {
        out.push({
          inputName,
          status: 'ambiguous',
          detail: `${matches.length} brand docs share this name; resolve in Firebase Console`,
        });
        continue;
      }

      const match = matches[0];
      const oldStatus = match.curationStatus;

      if (oldStatus === newStatus) {
        out.push({
          inputName,
          status: 'already',
          brandId: match.id,
          brandName: match.name,
          oldStatus,
          newStatus,
        });
        continue;
      }

      if (!dryRun) {
        try {
          await updateDoc(doc(db, 'brands', match.id), {
            // Use null to clear the field semantics-wise; Firestore stores
            // it as a real null, which our queries treat the same as
            // absent (both fall back to 'community' rank).
            curationStatus: newStatus,
            updatedAt: serverTimestamp(),
          });
        } catch (err: any) {
          out.push({
            inputName,
            status: 'error',
            brandId: match.id,
            brandName: match.name,
            detail: String(err?.message ?? err),
          });
          continue;
        }
      }

      out.push({
        inputName,
        status: 'ok',
        brandId: match.id,
        brandName: match.name,
        oldStatus,
        newStatus,
      });
    }

    setResults(out);
    setIsRunning(false);
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <p className="text-glass-secondary">Checking admin status...</p>
        </div>
      </div>
    );
  }

  const okCount = results.filter((r) => r.status === 'ok').length;
  const notFoundCount = results.filter((r) => r.status === 'not-found').length;
  const ambiguousCount = results.filter((r) => r.status === 'ambiguous').length;
  const alreadyCount = results.filter((r) => r.status === 'already').length;
  const errorCount = results.filter((r) => r.status === 'error').length;

  return (
    <div className="max-w-4xl mx-auto p-8">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      <div className="glass-card p-6">
        <h1 className="text-2xl font-bold text-glass mb-2 font-heading">
          🔖 Set Brand Curation Status (Admin)
        </h1>
        <p className="text-glass-secondary mb-4 font-body">
          Batch-mark brands as verified, community, or needs-review. Paste one
          brand name per line. Names are matched exactly first, then case-insensitively
          as a fallback. Brands ranked by curation status in search and autocomplete.
        </p>

        {/* Admin gate */}
        {!currentUser ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be logged in to use this tool!</p>
          </div>
        ) : !isAdmin ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be an admin to use this tool!</p>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
            <p className="text-green-300">✅ Admin access confirmed</p>
          </div>
        )}

        {/* Status selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
            Set status to
          </label>
          <select
            value={targetStatus}
            onChange={(e) => setTargetStatus(e.target.value as StatusChoice)}
            disabled={isRunning}
            className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-glass font-body"
          >
            <option value="verified">verified · canonical brand</option>
            <option value="community">community · auto-created</option>
            <option value="needs-review">needs-review · flag as suspicious</option>
            <option value="clear">(clear · back to default)</option>
          </select>
        </div>

        {/* Browse + pick — load the brand list once, filter client-side,
            click brands to add them to the input below. Faster than typing
            and avoids typo-induced "not found" results. */}
        {isAdmin && (
          <div className="mb-4 p-4 bg-slate-800/60 border border-slate-600 rounded-lg">
            <p className="text-sm font-medium text-glass-secondary mb-2 font-body">
              Browse brands
              {loadingBrands && <span className="text-glass-muted text-xs"> (loading…)</span>}
              {!loadingBrands && allBrands.length > 0 && (
                <span className="text-glass-muted text-xs"> ({allBrands.length} total)</span>
              )}
            </p>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={browseFilter}
                onChange={(e) => setBrowseFilter(e.target.value)}
                placeholder="Search by name or doc id"
                className="input-glass text-sm flex-1"
              />
              <select
                value={browseStatusFilter}
                onChange={(e) => setBrowseStatusFilter(e.target.value as any)}
                className="bg-slate-800 border border-slate-600 rounded px-2 py-2 text-glass font-body text-sm"
              >
                <option value="all">all statuses</option>
                <option value="verified">verified</option>
                <option value="community">community</option>
                <option value="needs-review">needs-review</option>
                <option value="default">(default)</option>
              </select>
            </div>
            {!loadingBrands && (() => {
              const filtered = filteredBrowse();
              return (
                <>
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {filtered.length === 0 ? (
                      <p className="text-xs text-glass-muted py-2 font-body italic">
                        No brands match.
                      </p>
                    ) : (
                      filtered.map((b) => {
                        const statusBadge =
                          b.curationStatus === 'verified'
                            ? '✅ verified'
                            : b.curationStatus === 'needs-review'
                              ? '⚠️ needs-review'
                              : b.curationStatus === 'community'
                                ? 'community'
                                : '(default)';
                        return (
                          <button
                            key={b.id}
                            onClick={() => addBrandToInput(b.name)}
                            className="w-full flex justify-between items-center text-left text-xs bg-slate-900/60 hover:bg-slate-700 px-3 py-2 rounded border border-slate-600 font-body"
                          >
                            <span className="text-glass">{b.name}</span>
                            <span className="text-glass-muted">{statusBadge}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-glass-muted mt-2 font-body italic">
                    {filtered.length} of {allBrands.length} brands — scroll the list, or use search / status filter to narrow.
                  </p>
                </>
              );
            })()}
          </div>
        )}

        {/* Names input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
            Brand names (one per line)
          </label>
          <textarea
            value={brandsInput}
            onChange={(e) => setBrandsInput(e.target.value)}
            disabled={isRunning}
            rows={10}
            className="input-glass w-full font-mono text-sm"
            placeholder="Cracked Polish&#10;Ethereal Lacquer&#10;ILNP [I Love Nail Polish]&#10;O•P•I Nail Lacquer"
          />
          <p className="text-xs text-glass-muted mt-1">
            {parsedNames().length} unique brand{parsedNames().length === 1 ? '' : 's'}
          </p>
        </div>

        {/* Buttons */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => run(true)}
            disabled={isRunning || !isAdmin || parsedNames().length === 0}
            className={`px-5 py-2.5 rounded-lg font-medium text-white transition-colors font-body ${
              isRunning || !isAdmin || parsedNames().length === 0
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            🔎 Preview (dry run)
          </button>
          <button
            onClick={() => run(false)}
            disabled={isRunning || !isAdmin || parsedNames().length === 0}
            className={`px-5 py-2.5 rounded-lg font-medium text-white transition-colors font-body ${
              isRunning || !isAdmin || parsedNames().length === 0
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {isRunning ? 'Working…' : '✅ Apply'}
          </button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div>
            <div className="mb-3 flex flex-wrap gap-3 text-sm">
              <span className="text-green-300">✅ ok: {okCount}</span>
              <span className="text-blue-300">↻ already set: {alreadyCount}</span>
              {notFoundCount > 0 && (
                <span className="text-red-300">❌ not found: {notFoundCount}</span>
              )}
              {ambiguousCount > 0 && (
                <span className="text-yellow-300">⚠️ ambiguous: {ambiguousCount}</span>
              )}
              {errorCount > 0 && (
                <span className="text-red-300">💥 errors: {errorCount}</span>
              )}
            </div>
            <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto border border-slate-700">
              {results.map((r, i) => {
                const icon =
                  r.status === 'ok'
                    ? '✅'
                    : r.status === 'already'
                      ? '↻'
                      : r.status === 'not-found'
                        ? '❌'
                        : r.status === 'ambiguous'
                          ? '⚠️'
                          : '💥';
                return (
                  <div key={i} className="mb-1">
                    {icon}{' '}
                    <span className="text-glass">{r.inputName}</span>
                    {r.brandName && r.brandName !== r.inputName && (
                      <span className="text-glass-muted"> → {r.brandName}</span>
                    )}
                    {r.brandId && (
                      <span className="text-glass-muted"> ({r.brandId})</span>
                    )}
                    {r.status === 'ok' && (
                      <span className="text-glass-muted">
                        {' '}
                        · {r.oldStatus ?? '(none)'} → {r.newStatus ?? '(cleared)'}
                      </span>
                    )}
                    {r.status === 'already' && (
                      <span className="text-glass-muted">
                        {' '}
                        · already {r.oldStatus ?? '(cleared)'}
                      </span>
                    )}
                    {r.detail && (
                      <span className="text-yellow-300"> — {r.detail}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
          <p className="text-blue-300 text-sm font-body">
            💡 <strong className="text-blue-200">Tip:</strong> Use "Preview" first
            to verify names resolve correctly. Names that come back "not found" usually
            mean the brand record has a slightly different spelling — check Firebase
            Console or use the brand page URL slug to confirm exact name.
          </p>
        </div>
      </div>
    </div>
  );
}
